import * as yup from 'yup';
import {
  EIN_REGEX,
  FOREIGN_ENTITY_REGISTRATION_REGEX,
  FOREIGN_PASSPORT_REGEX,
  FOREIGN_TAX_ID_REGEX,
  SSN_ITIN_REGEX,
} from '../../constants/validation-patterns';
import {
  requiresStateAndPostalCode,
  isValidStateForCountry,
  validatePostalCodeFormat,
  isUSTerritory,
  isUS,
} from '@agentnet/utils';

export enum TransfereeType {
  ENTITY = 'entity',
  TRUST = 'trust',
}

export enum PartyType {
  TRUSTEE_LEGAL_ENTITY = 'trusteeLegalEntity',
  BENEFICIAL_OWNER = 'beneficialOwner',
  TRUSTEE = 'trustee',
  SIGNING_INDIVIDUAL = 'signingIndividual',
}

export enum IdentificationType {
  SSN_ITIN = 'ssn-itin',
  EIN = 'ein',
  FOREIGN_TAX_ID = 'foreignTaxId',
  FOREIGN_PASSPORT = 'foreignPassport',
  FOREIGN_ENTITY_REGISTRATION_NUMBER = 'foreignEntityRegistrationNumber',
  NO_IDENTIFICATION = 'noIdentification',
}

export enum AddressType {
  PRIMARY = 'primary',
  FOREIGN = 'foreign',
}

const requiredFieldMessage = (fieldName: string): string => `${fieldName} is a required field.`;

export const addressSchema = yup.object().shape({
  address1: yup
    .string()
    .trim()
    .max(100, 'Address 1 cannot exceed 100 characters')
    .required(requiredFieldMessage('Address 1')),
  address2: yup
    .string()
    .trim()
    .test('max-length', 'Address 2 cannot exceed 100 characters', (value) => !value || value.length <= 100)
    .notRequired(),
  city: yup.string().trim().max(50, 'City cannot exceed 50 characters').required(requiredFieldMessage('City')),
  country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
  state: yup.string().when(['country', '$foreignBusinessNoUSLocation'], {
    is: (country: string, foreignBusinessNoUSLocation: boolean) =>
      requiresStateAndPostalCode(country) && !foreignBusinessNoUSLocation,
    then: (schema) =>
      schema
        .trim()
        .required(requiredFieldMessage('State/U.S. Territory'))
        .test('valid-state-for-country', function (value) {
          const { country } = this.parent;
          if (!value || !country) return true;

          if (!isValidStateForCountry(value, country)) {
            return this.createError({
              message: 'Invalid state/province code for the selected country',
            });
          }
          return true;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  postalCode: yup
    .string()
    .trim()
    .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { country } = this.parent;
      // US and US Territories allow ZIP+4 format (10 chars with hyphen)
      if (isUS(country) || isUSTerritory(country)) {
        return trimmedValue.length <= 10;
      }
      return trimmedValue.length <= 9;
    })
    .test('format-validation', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { country } = this.parent;

      const error = validatePostalCodeFormat(trimmedValue, country);
      if (error) {
        return this.createError({ message: error });
      }
      return true;
    })
    .when('country', {
      is: (country: string) => requiresStateAndPostalCode(country),
      then: (schema) => schema.required(requiredFieldMessage('Zip/Postal Code')),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const foreignAddressSchema = yup.object().shape({
  address1: yup
    .string()
    .trim()
    .max(100, 'Address 1 cannot exceed 100 characters')
    .required(requiredFieldMessage('Address 1')),
  address2: yup
    .string()
    .trim()
    .test('max-length', 'Address 2 cannot exceed 100 characters', (value) => !value || value.length <= 100)
    .notRequired(),
  city: yup.string().trim().max(50, 'City cannot exceed 50 characters').required(requiredFieldMessage('City')),
  country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
  state: yup.string().when('country', {
    is: (country: string) => requiresStateAndPostalCode(country),
    then: (schema) =>
      schema
        .trim()
        .required(requiredFieldMessage('State/U.S. Territory'))
        .test('valid-state-for-country', function (value) {
          const { country } = this.parent;
          if (!value || !country) return true;

          if (!isValidStateForCountry(value, country)) {
            return this.createError({
              message: 'Invalid state/province code for the selected country',
            });
          }
          return true;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  postalCode: yup
    .string()
    .trim()
    .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { country } = this.parent;
      // US and US Territories allow ZIP+4 format (10 chars with hyphen)
      if (isUS(country) || isUSTerritory(country)) {
        return trimmedValue.length <= 10;
      }
      return trimmedValue.length <= 9;
    })
    .test('format-validation', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { country } = this.parent;

      const error = validatePostalCodeFormat(trimmedValue, country);
      if (error) {
        return this.createError({ message: error });
      }
      return true;
    })
    .when('country', {
      is: (country: string) => requiresStateAndPostalCode(country),
      then: (schema) => schema.required(requiredFieldMessage('Zip/Postal Code')),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const partySchema = yup.object().shape({
  partyType: yup.string().required(requiredFieldMessage('Party Type')),

  entityLegalName: yup.string().when('partyType', {
    is: PartyType.TRUSTEE_LEGAL_ENTITY,
    then: (schema) =>
      schema
        .trim()
        .max(130, 'Entity Legal Name cannot exceed 130 characters')
        .required(requiredFieldMessage('Entity Legal Name')),
    otherwise: (schema) => schema.notRequired(),
  }),
  entityType: yup.string().when('partyType', {
    is: PartyType.TRUSTEE_LEGAL_ENTITY,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.notRequired(),
  }),
  alternateNames: yup
    .mixed()
    .nullable()
    .when('partyType', {
      is: PartyType.TRUSTEE_LEGAL_ENTITY,
      then: (schema) =>
        schema.test('max-length-per-name', 'Alternate Name cannot exceed 150 characters', function (value) {
          if (!value) return true;
          let names: string[] = [];
          // Handle both string (UI format) and array (API format)
          if (typeof value === 'string') {
            names = value.split(',').map((name) => name.trim());
          } else if (Array.isArray(value)) {
            names = value.map((name) => (typeof name === 'string' ? name.trim() : ''));
          } else {
            return true;
          }
          return names.every((name) => name.length <= 150);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
  firstName: yup.string().when('partyType', {
    is: PartyType.TRUSTEE_LEGAL_ENTITY,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.trim().max(50, 'First Name cannot exceed 50 characters').required(requiredFieldMessage('First Name')),
  }),
  middleName: yup
    .string()
    .trim()
    .test('max-length', 'Middle Name cannot exceed 50 characters', (value) => !value || value.length <= 50)
    .notRequired(),
  lastName: yup.string().when('partyType', {
    is: PartyType.TRUSTEE_LEGAL_ENTITY,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.trim().max(50, 'Last Name cannot exceed 50 characters').required(requiredFieldMessage('Last Name')),
  }),
  suffix: yup
    .string()
    .trim()
    .test('max-length', 'Suffix cannot exceed 20 characters', (value) => !value || value.length <= 20)
    .notRequired(),
  dateOfBirth: yup.date().when('partyType', {
    is: PartyType.TRUSTEE_LEGAL_ENTITY,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .max(new Date(), 'Date of Birth cannot be in the future')
        .typeError('Please enter a valid date')
        .required(requiredFieldMessage('Date of Birth')),
  }),

  authorizationCapacity: yup.string().notRequired(),
  authorizationOtherDescription: yup.string().when('authorizationCapacity', {
    is: 'other',
    then: (schema) => schema.trim().required(requiredFieldMessage('Other Description')),
    otherwise: (schema) => schema.notRequired(),
  }),
  employerName: yup.string().when('authorizationCapacity', {
    is: (value: string) => value && value !== 'other',
    then: (schema) =>
      schema
        .trim()
        .max(150, 'Name of Employer, Principal,Partnership, etc. cannot exceed 150 characters')
        .required(requiredFieldMessage('Name of Employer, Principal,Partnership, etc.')),
    otherwise: (schema) => schema.notRequired(),
  }),

  addressType: yup.string().when(['partyType', 'foreignBusinessNoUSLocation'], {
    is: (partyType: string, foreignBusinessNoUSLocation: boolean) =>
      partyType === PartyType.TRUSTEE_LEGAL_ENTITY && !foreignBusinessNoUSLocation,
    then: (schema) => schema.required(requiredFieldMessage('Address Type')),
    otherwise: (schema) => schema.notRequired(),
  }),

  address: addressSchema,

  foreignPrincipalAddress: yup.object().when(['partyType', 'addressType'], {
    is: (partyType: string, addressType: string) =>
      partyType === PartyType.TRUSTEE_LEGAL_ENTITY && addressType === AddressType.PRIMARY,
    then: () => foreignAddressSchema,
    otherwise: (schema) => schema.notRequired(),
  }),

  foreignBusinessNoUSLocation: yup.boolean().notRequired(),

  identificationType: yup.string().required(requiredFieldMessage('Type of Unique Identifying Number')),
  identificationNumber: yup.string().when('identificationType', {
    is: (value: string) => value && value !== IdentificationType.NO_IDENTIFICATION,
    then: (schema) =>
      schema
        .trim()
        .required(requiredFieldMessage('Unique Identifying Number'))
        .test('format-validation', function (value) {
          const { identificationType } = this.parent;
          if (!value) return true;

          const trimmedValue = value.trim();

          if (identificationType === IdentificationType.EIN) {
            if (!EIN_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'EIN must be in XX-XXXXXXX format (e.g., 12-3456789)',
              });
            }
          }

          if (identificationType === IdentificationType.SSN_ITIN) {
            if (!SSN_ITIN_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'SSN/ITIN must be in XXX-XX-XXXX format (e.g., 123-45-6789)',
              });
            }
          }

          if (identificationType === IdentificationType.FOREIGN_TAX_ID) {
            if (!FOREIGN_TAX_ID_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'Foreign Tax ID must be 6-20 alphanumeric characters only (e.g., ABC123456)',
              });
            }
          }

          if (identificationType === IdentificationType.FOREIGN_PASSPORT) {
            if (!FOREIGN_PASSPORT_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'Foreign Passport Number must be 6-9 alphanumeric characters only (e.g., A1234567)',
              });
            }
          }

          if (identificationType === IdentificationType.FOREIGN_ENTITY_REGISTRATION_NUMBER) {
            if (!FOREIGN_ENTITY_REGISTRATION_REGEX.test(trimmedValue)) {
              return this.createError({
                message:
                  'Foreign Entity Registration Number must be 5-20 alphanumeric characters only (e.g., ABC12345)',
              });
            }
          }

          return true;
        })
        .max(50, 'Identification Number cannot exceed 50 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  issuingJurisdiction: yup.string().when('identificationType', {
    is: (value: string) =>
      value &&
      (value === IdentificationType.FOREIGN_TAX_ID ||
        value === IdentificationType.FOREIGN_PASSPORT ||
        value === IdentificationType.FOREIGN_ENTITY_REGISTRATION_NUMBER),
    then: (schema) => schema.required(requiredFieldMessage('Issuing Jurisdiction')),
    otherwise: (schema) => schema.notRequired(),
  }),

  beneficialOwnerCategory: yup.string().when(['partyType', '$transfereeType'], {
    is: (partyType: string, transfereeType: string) =>
      (partyType === 'beneficialOwner' || partyType === 'bothBeneficialOwnerAndSigningIndividual') &&
      transfereeType !== TransfereeType.ENTITY,
    then: (schema) => schema.required(requiredFieldMessage('Beneficial Owner Category')),
    otherwise: (schema) => schema.notRequired(),
  }),
  citizenshipCountry: yup
    .array()
    .of(yup.string())
    .when('partyType', {
      is: (partyType: string) =>
        partyType === 'beneficialOwner' || partyType === 'bothBeneficialOwnerAndSigningIndividual',
      then: (schema) =>
        schema.min(1, 'Citizenship Country is a required field.').required(requiredFieldMessage('Citizenship Country')),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const transfereeSchema = yup.object().shape({
  transfereeType: yup
    .string()
    .oneOf([TransfereeType.ENTITY, TransfereeType.TRUST])
    .required(requiredFieldMessage('Transferee Type')),
  transfereeLegalName: yup
    .string()
    .trim()
    .max(130, 'Transferee Legal Name cannot exceed 130 characters')
    .required(requiredFieldMessage('Transferee Legal Name')),
  transfereeDbaName: yup
    .mixed()
    .nullable()
    .when('transfereeType', {
      is: TransfereeType.ENTITY,
      then: (schema) =>
        schema.test('max-length-per-name', 'Alternate Name cannot exceed 150 characters', function (value) {
          if (!value) return true;
          let names: string[] = [];
          if (typeof value === 'string') {
            names = value.split(',').map((name) => name.trim());
          } else if (Array.isArray(value)) {
            names = value.map((name) => (typeof name === 'string' ? name.trim() : ''));
          } else {
            return true;
          }
          return names.every((name) => name.length <= 150);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
  noConsiderationPaid: yup.boolean().notRequired(),
  totalConsideration: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .when('noConsiderationPaid', {
      is: false,
      then: (schema) =>
        schema
          .min(0.01, 'Total consideration must be greater than zero')
          .required(requiredFieldMessage('Total Consideration')),
      otherwise: (schema) => schema.notRequired(),
    }),

  foreignBusinessNoUSLocation: yup.boolean().notRequired(),

  foreignBusinessAddress: yup.object().when(['transfereeType', 'foreignBusinessNoUSLocation'], {
    is: (transfereeType: string, foreignBusinessNoUSLocation: boolean) =>
      transfereeType === TransfereeType.ENTITY && foreignBusinessNoUSLocation === true,
    then: () => addressSchema,
    otherwise: (schema) => schema.notRequired(),
  }),

  addressType: yup.string().when(['partyType', 'foreignBusinessNoUSLocation'], {
    is: (partyType: string, foreignBusinessNoUSLocation: boolean) =>
      partyType === PartyType.TRUSTEE_LEGAL_ENTITY && !foreignBusinessNoUSLocation,
    then: (schema) => schema.required(requiredFieldMessage('Address Type')),
    otherwise: (schema) => schema.notRequired(),
  }),

  foreignPrincipalAddress: yup.object().when(['transfereeType', 'addressType'], {
    is: (transfereeType: string, addressType: string) =>
      transfereeType === TransfereeType.ENTITY && addressType === AddressType.PRIMARY,
    then: () => foreignAddressSchema,
    otherwise: (schema) => schema.notRequired(),
  }),

  primaryUSAddress: yup.object().when(['transfereeType', 'addressType'], {
    is: (transfereeType: string, addressType: string) =>
      transfereeType === TransfereeType.ENTITY &&
      (addressType === AddressType.PRIMARY || addressType === AddressType.FOREIGN),
    then: () => addressSchema,
    otherwise: (schema) => schema.notRequired(),
  }),

  identificationType: yup.string().required(requiredFieldMessage('Type of Unique Identifying Number')),
  identificationNumber: yup.string().when('identificationType', {
    is: (value: string) => value && value !== IdentificationType.NO_IDENTIFICATION,
    then: (schema) =>
      schema
        .trim()
        .required(requiredFieldMessage('Unique Identifying Number'))
        .test('format-validation', function (value) {
          const { identificationType } = this.parent;
          if (!value) return true;
          const trimmedValue = value.trim();

          if (identificationType === IdentificationType.EIN) {
            if (!EIN_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'EIN must be in XX-XXXXXXX format (e.g., 12-3456789)',
              });
            }
          }

          if (identificationType === IdentificationType.SSN_ITIN) {
            if (!SSN_ITIN_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'SSN/ITIN must be in XXX-XX-XXXX format (e.g., 123-45-6789)',
              });
            }
          }

          if (identificationType === IdentificationType.FOREIGN_TAX_ID) {
            if (!FOREIGN_TAX_ID_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'Foreign Tax ID must be 6-20 alphanumeric characters only (e.g., ABC123456)',
              });
            }
          }

          if (identificationType === IdentificationType.FOREIGN_PASSPORT) {
            if (!FOREIGN_PASSPORT_REGEX.test(trimmedValue)) {
              return this.createError({
                message: 'Foreign Passport Number must be 6-9 alphanumeric characters only (e.g., A1234567)',
              });
            }
          }

          if (identificationType === IdentificationType.FOREIGN_ENTITY_REGISTRATION_NUMBER) {
            if (!FOREIGN_ENTITY_REGISTRATION_REGEX.test(trimmedValue)) {
              return this.createError({
                message:
                  'Foreign Entity Registration Number must be 5-20 alphanumeric characters only (e.g., ABC12345)',
              });
            }
          }

          return true;
        })
        .max(50, 'Identification Number cannot exceed 50 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  issuingJurisdiction: yup.string().when('identificationType', {
    is: (value: string) =>
      value &&
      (value === IdentificationType.FOREIGN_TAX_ID ||
        value === IdentificationType.FOREIGN_PASSPORT ||
        value === IdentificationType.FOREIGN_ENTITY_REGISTRATION_NUMBER),
    then: (schema) => schema.required(requiredFieldMessage('Issuing Jurisdiction')),
    otherwise: (schema) => schema.notRequired(),
  }),

  dateTrustExecuted: yup
    .date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('transfereeType', {
      is: TransfereeType.TRUST,
      then: (schema) =>
        schema
          .max(new Date(), 'Date Trust Instrument was Executed cannot be in the future')
          .typeError('Please enter a valid date')
          .required(requiredFieldMessage('Date Trust Instrument was Executed')),
      otherwise: (schema) => schema.notRequired(),
    }),
  isRevocableTrust: yup.boolean().notRequired(),

  parties: yup
    .array()
    .test('validate-parties-with-transferee-type', 'Invalid party data', function (parties) {
      if (!parties || parties.length === 0) return true;

      const transfereeType = this.parent.transfereeType;
      const errors: any[] = [];

      parties.forEach((party: any, index: number) => {
        try {
          partySchema.validateSync(party, { abortEarly: false, context: { transfereeType } });
        } catch (err: any) {
          if (err.inner && err.inner.length > 0) {
            err.inner.forEach((error: any) => {
              errors.push(
                this.createError({
                  path: `${this.path}[${index}].${error.path}`,
                  message: error.message,
                }),
              );
            });
          }
        }
      });

      if (errors.length > 0) {
        throw new yup.ValidationError(errors);
      }

      return true;
    })
    .notRequired(),
});

export default transfereeSchema;
